"use client";

import type React from "react";
import { type ReactNode } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils/index";
import { FetchButton } from "@/fetch-ui/FetchButton";

interface FetchEmptyStateAction {
  /** Button label text */
  label: string;
  /** Click handler for the action button */
  onClick: () => void;
  /** Button variant. Defaults to "outline" (existing behavior). */
  variant?: "outline" | "ghost";
  /** Hide the leading plus icon. Defaults to false (icon shown). */
  hideIcon?: boolean;
}

interface FetchEmptyStateProps {
  /** Primary message, e.g. "No forms yet" */
  title: string;
  /** Secondary message, e.g. "Create a form to get started" */
  subtitle?: string;
  /** Optional action button rendered below the text */
  action?: FetchEmptyStateAction;
  /** Optional icon rendered above the title */
  icon?: ReactNode;
  /** Optional className override for the outer container */
  className?: string;
}

/**
 * Dashed-border empty state placeholder with optional subtitle and action button.
 * Used when a list or grid has no items to display.
 */
export function FetchEmptyState({
  title,
  subtitle,
  action,
  icon,
  className,
}: FetchEmptyStateProps): React.ReactElement {
  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-16",
        className
      )}
    >
      {icon && <div className="mb-3">{icon}</div>}
      <p className="text-sm font-medium text-gray-700">{title}</p>
      {subtitle && <p className="mt-1 max-w-sm text-center text-xs font-medium text-gray-500">{subtitle}</p>}
      {action && (
        <FetchButton
          variant={action.variant ?? "outline"}
          size="sm"
          leftIcon={action.hideIcon ? undefined : <Plus className="h-3.5 w-3.5" />}
          onClick={action.onClick}
          className="mt-4"
        >
          {action.label}
        </FetchButton>
      )}
    </div>
  );
}
